import { ArrowRight, Sparkles, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiPost } from '../api/client.js';
import { Button } from './Button.js';
import { Card } from './Card.js';
import { Skeleton } from './Skeleton.js';

interface Suggestion {
  title: string;
  category: string;
  demand: 'Low' | 'Medium' | 'High';
  priceMin: number;
  priceMax: number;
  supplier: string;
  reasoning: string;
}

const DEMAND_TONE: Record<Suggestion['demand'], { bg: string; fg: string }> = {
  High: { bg: 'var(--c-success-bg)', fg: 'var(--c-success)' },
  Medium: { bg: 'var(--c-warn-bg)', fg: 'var(--c-warn)' },
  Low: { bg: 'var(--c-divider)', fg: 'var(--c-muted)' },
};

const SUPPLIER_COLOR: Record<string, string> = {
  AliExpress: '#e62e04',
  Amazon: '#ff9900',
  Walmart: '#0071ce',
  'CJ Dropshipping': '#0066cc',
  Other: '#475569',
};

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function SuggestionCard({ item }: { item: Suggestion }): JSX.Element {
  const tone = DEMAND_TONE[item.demand];
  const supplierColor = SUPPLIER_COLOR[item.supplier] ?? SUPPLIER_COLOR.Other ?? '#475569';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--c-card)',
        boxShadow: 'var(--shadow-xs)',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--c-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          {item.category}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--text-xs)',
            background: tone.bg,
            color: tone.fg,
            padding: '2px 8px',
            borderRadius: 99,
            fontWeight: 600,
          }}
        >
          <TrendingUp size={11} strokeWidth={2.4} />
          {item.demand} demand
        </span>
      </div>

      <div>
        <div
          style={{
            fontSize: 'var(--text-md)',
            fontWeight: 600,
            color: 'var(--c-text)',
            lineHeight: 1.3,
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--c-muted)',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {item.reasoning}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 8,
          borderTop: '1px solid var(--c-divider)',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--c-muted)' }}>Sell price</div>
          <div style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
            {fmt(item.priceMin)} – {fmt(item.priceMax)}
          </div>
        </div>
        <span
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: '#fff',
            background: supplierColor,
            padding: '4px 10px',
            borderRadius: 99,
          }}
        >
          {item.supplier}
        </span>
      </div>

      <Link
        to={`/research?q=${encodeURIComponent(item.title)}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          height: 32,
          fontSize: 'var(--text-base)',
          fontWeight: 500,
          background: 'var(--c-primary-soft)',
          color: 'var(--c-primary)',
          borderRadius: 'var(--radius-md)',
          textDecoration: 'none',
        }}
      >
        Research this
        <ArrowRight size={13} strokeWidth={2.4} />
      </Link>
    </div>
  );
}

export function SuggestionsCard({ storeName }: { storeName: string }): JSX.Element {
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestions = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiPost<{ items: Suggestion[] }>('/api/ai/suggestions', {
        store: storeName,
        count: 8,
      });
      setItems(res.items);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Suggestion request failed.';
      setError(
        msg.includes('No AI provider configured')
          ? 'AI is not configured yet. Open Admin and pick a provider.'
          : msg,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="AI suggestions"
      description="Fresh product ideas for your Canadian stores. Verify on eBay or AliExpress before listing."
      action={
        <Button size="sm" icon={Sparkles} loading={loading} onClick={fetchSuggestions}>
          {items ? 'Refresh' : 'Suggest products'}
        </Button>
      }
    >
      {loading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={200} />
          ))}
        </div>
      )}
      {error && (
        <div
          style={{
            padding: 12,
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-danger-bg)',
            color: 'var(--c-danger)',
            fontSize: 'var(--text-md)',
          }}
        >
          {error}
        </div>
      )}
      {items && !loading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {items.map((item, i) => (
            <SuggestionCard key={`${item.title}-${i}`} item={item} />
          ))}
        </div>
      )}
      {!items && !loading && !error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 16,
            borderRadius: 'var(--radius-md)',
            background: 'var(--c-divider)',
            color: 'var(--c-muted)',
            fontSize: 'var(--text-md)',
          }}
        >
          <Sparkles size={18} />
          <span>Click Suggest products to get 8 fresh ideas with demand, price, and supplier.</span>
        </div>
      )}
    </Card>
  );
}
